package diana.soleil.design.iraniancanadienbazzar.feature.gems

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import diana.soleil.design.iraniancanadienbazzar.core.common.AppError
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.common.Formatters
import diana.soleil.design.iraniancanadienbazzar.core.data.GemsRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.GemsSavedController
import diana.soleil.design.iraniancanadienbazzar.core.data.ReportRepository
import diana.soleil.design.iraniancanadienbazzar.core.model.Fact
import diana.soleil.design.iraniancanadienbazzar.core.model.FactCategory
import diana.soleil.design.iraniancanadienbazzar.core.model.GemType
import diana.soleil.design.iraniancanadienbazzar.core.model.SavedGem
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch

/**
 * Danestaniha browser state. [facts] is the filtered/searched pager list; [totalCount] is the
 * corpus size (for the "3/40" counter).
 */
data class DanestanihaUiState(
    val loading: Boolean = true,
    val error: Boolean = false,
    val facts: List<Fact> = emptyList(),
    val totalCount: Int = 0,
    /** Selected topic chip; null = All. */
    val category: FactCategory? = null,
    val query: String = "",
    val likedFactIds: Set<String> = emptySet(),
    val savedFactIds: Set<String> = emptySet(),
    /** Guest tried to like — the route sends them to sign-in. */
    val signInRequired: Boolean = false,
    val reportStatus: GemReportStatus = GemReportStatus.Idle,
)

/**
 * Danestaniha (دانستنی‌ها) swipe reader — a full-screen vertical pager of "did you know?" facts
 * about Iran, Canada and the Iranian-Canadian community. Mirrors [JokesViewModel] (streamed corpus
 * + trilingual client-side search over [Formatters.normalizePersian]; the corpus is small by
 * design) but has no maturity tier — every fact is family-safe by construction.
 */
class DanestanihaViewModel(
    private val gemsRepository: GemsRepository,
    private val savedController: GemsSavedController,
    private val reportRepository: ReportRepository,
) : ViewModel() {

    private val categoryFlow = MutableStateFlow<FactCategory?>(null)
    private val queryFlow = MutableStateFlow("")
    private val signInGate = MutableStateFlow(false)
    private val reportStatus = MutableStateFlow(GemReportStatus.Idle)

    val state: StateFlow<DanestanihaUiState> =
        combine(
            gemsRepository.observeFacts(),
            gemsRepository.observeLikedFactIds(),
            savedController.saved,
            combine(categoryFlow, queryFlow) { cat, query -> cat to query },
            combine(signInGate, reportStatus) { signIn, report -> signIn to report },
        ) { result, liked, saved, (category, query), (signIn, report) ->
            when (result) {
                is DataResult.Success -> DanestanihaUiState(
                    loading = false,
                    facts = result.data.applyFactFilters(category, query),
                    totalCount = result.data.size,
                    category = category,
                    query = query,
                    likedFactIds = liked,
                    savedFactIds = saved.filter { it.type == GemType.Fact }.map { it.id }.toSet(),
                    signInRequired = signIn,
                    reportStatus = report,
                )
                is DataResult.Failure -> DanestanihaUiState(
                    loading = false,
                    error = true,
                    signInRequired = signIn,
                    reportStatus = report,
                )
            }
        }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), DanestanihaUiState())

    fun setCategory(category: FactCategory?) {
        categoryFlow.value = category
    }

    fun onQueryChange(query: String) {
        queryFlow.value = query
    }

    /** Toggles the caller's like; guests bounce to sign-in via the gate (double-tap or rail). */
    fun toggleLike(factId: String, liked: Boolean) {
        viewModelScope.launch {
            val result = gemsRepository.setFactLiked(factId, !liked)
            if (result is DataResult.Failure && result.error is AppError.Unauthorized) {
                signInGate.value = true
            }
        }
    }

    /** Toggles the private bookmark — works for guests too (device-local, cloud when signed in). */
    fun toggleSave(factId: String) {
        viewModelScope.launch { savedController.toggle(SavedGem(id = factId, type = GemType.Fact)) }
    }

    fun consumeSignInGate() {
        signInGate.value = false
    }

    fun report(factId: String, reason: String) {
        if (reportStatus.value == GemReportStatus.Sending) return
        viewModelScope.launch {
            reportStatus.value = GemReportStatus.Sending
            reportStatus.value = when (val result = reportRepository.fileReport("fact", factId, reason)) {
                is DataResult.Success -> GemReportStatus.Sent
                is DataResult.Failure ->
                    if (result.error is AppError.Unauthorized) GemReportStatus.SignInRequired
                    else GemReportStatus.Failed
            }
        }
    }

    fun resetReport() {
        reportStatus.value = GemReportStatus.Idle
    }
}

private fun List<Fact>.applyFactFilters(category: FactCategory?, query: String): List<Fact> {
    val normalized = Formatters.normalizePersian(query).trim().lowercase()
    val tokens = normalized.split(' ').filter { it.isNotBlank() }
    return filter { fact ->
        (category == null || fact.category == category) &&
            (tokens.isEmpty() || tokens.all { token -> fact.searchHaystack().contains(token) })
    }
}

/** Lowercased, Persian-normalized bag of every text variant + source + tags (SearchViewModel pattern). */
private fun Fact.searchHaystack(): String =
    Formatters.normalizePersian(
        buildList {
            addAll(listOfNotNull(text.en, text.fr, text.fa))
            addAll(listOfNotNull(source?.en, source?.fr, source?.fa))
            addAll(tags)
        }.joinToString(" "),
    ).lowercase()
